"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border-2 border-primary rounded-lg shadow-medium">
        <p className="text-foreground text-lg font-semibold mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required",
        pattern: {
          value: /^[0-9\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(123) 456-7890" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your fire protection needs..." rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
            <p className="text-red-800 text-sm font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-foreground-primary font-semibold py-3 rounded-md shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          {isSubmitting ? <>
              <span className="animate-spin mr-2">⏳</span>
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section ref={ref} className={`py-16 px-4 bg-light-background transition-opacity duration-[300ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {}
          <FormProvider className="bg-light-background p-8 rounded-lg shadow-soft border border-border" formId="contact-form-hero" sectionName="contact-form" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you! Mike will get back to you soon.">
            <ContactFormInner />
          </FormProvider>

          {}
          <div className="space-y-6">
            <Card className="border-2 border-primary shadow-medium hover:shadow-strong transition-all duration-[var(--duration-medium)]">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-foreground">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-3 rounded-md">
                    <Phone className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="text-lg font-semibold text-foreground">+1(571) 330 8388</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-3 rounded-md">
                    <Mail className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-lg font-semibold text-foreground">Mike@aplusfire.pro</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {}
            <div className={`relative overflow-hidden rounded-lg shadow-medium transition-all duration-[300ms] ease-[cubic-bezier(0.4,0,0.2,1)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[20px]"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/northern_virginia_service_map.png" alt="Northern Virginia Service Area Map" className="w-full h-64 object-cover rounded-lg" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-background/60 to-transparent rounded-lg"></div>
              <div className="absolute bottom-4 left-4 text-foreground-dark">
                <p className="text-lg font-bold">Serving Northern Virginia</p>
                <p className="text-sm">Arlington, Fairfax, Alexandria & More</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}